import type { AbilityName } from '@character-creator/types';
import type { CharacterBenefits } from '../../../hooks/useCharacterBenefits';
import { ABILITY_LABELS } from './constants';

interface AbilityScoresColumnProps {
  benefits: CharacterBenefits;
  onRollAbility: (label: string, modifier: number) => void;
  getSaveMod: (ability: AbilityName) => { mod: number; isProficient: boolean };
}

export function AbilityScoresColumn({
  benefits,
  onRollAbility,
  getSaveMod,
}: AbilityScoresColumnProps) {
  return (
    <div className="space-y-2">
      {/* Legend for saving throw proficiency indicator */}
      <div className="text-[10px] text-text-muted text-center mb-1">
        <span className="inline-flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-accent-gold border border-accent-gold" />
          = Save proficiency
        </span>
      </div>
      {Object.entries(benefits.modifiers).map(([ability, mod]) => {
        const { isProficient } = getSaveMod(ability as AbilityName);
        const score = benefits.finalScores[ability as keyof typeof benefits.finalScores];

        return (
          <button
            key={ability}
            onClick={() => onRollAbility(`${ABILITY_LABELS[ability]} Check`, mod)}
            className="w-full bg-background-card border border-background-elevated rounded-lg p-3 text-center
              hover:border-accent-bronze/30 transition-colors active:scale-[0.98]"
          >
            <div className="flex items-center justify-center gap-1 text-[10px] text-accent-gold uppercase tracking-wider mb-1">
              <span
                className={`w-2 h-2 rounded-full border ${
                  isProficient ? 'bg-accent-gold border-accent-gold' : 'border-text-muted'
                }`}
              />
              {ability}
            </div>
            <div className="font-mono text-2xl font-bold text-text-primary">
              {mod >= 0 ? '+' : ''}{mod}
            </div>
            <div className="font-mono text-xs text-text-muted">{score}</div>
          </button>
        );
      })}

      {/* Proficiency Bonus */}
      <div className="bg-background-card border border-background-elevated rounded-lg p-3 text-center">
        <div className="text-[10px] text-accent-gold uppercase tracking-wider mb-1">
          Proficiency
        </div>
        <div className="font-mono text-2xl font-bold text-accent-gold">
          +{benefits.proficiencyBonus}
        </div>
      </div>
    </div>
  );
}
